<template>
  <section class="propagation-panel toolbarSwitches" aria-label="Custom orbital propagation">
    <div class="toolbarTitle">Custom propagation</div>
    <p class="propagation-panel__intro">Numerical RK4 scenario rendered in the active Cesium globe.</p>

    <div class="propagation-panel__grid">
      <label>Altitude <span><input v-model.number="scenario.altitudeKm" type="number" min="160" max="50_000" step="10" /> km</span></label>
      <label>Eccentricity <input v-model.number="scenario.eccentricity" type="number" min="0" max="0.95" step="0.001" /></label>
      <label>Inclination <span><input v-model.number="scenario.inclinationDeg" type="number" min="0" max="180" step="0.1" />°</span></label>
      <label>RAAN <span><input v-model.number="scenario.raanDeg" type="number" min="0" max="360" step="1" />°</span></label>
      <label>Arg. periapsis <span><input v-model.number="scenario.argumentOfPeriapsisDeg" type="number" min="0" max="360" step="1" />°</span></label>
      <label>True anomaly <span><input v-model.number="scenario.trueAnomalyDeg" type="number" min="0" max="360" step="1" />°</span></label>
      <label>Duration <span><input v-model.number="durationMinutes" type="number" min="1" max="2_880" step="5" /> min</span></label>
      <label>Samples <input v-model.number="scenario.sampleCount" type="number" min="120" max="2_000" step="60" /></label>
    </div>

    <div class="toolbarTitle">Force models</div>
    <label class="toolbarSwitch"><input v-model="scenario.forces.gravity" type="checkbox" /><span class="slider"></span>Central gravity</label>
    <label class="toolbarSwitch"><input v-model="scenario.forces.j2" type="checkbox" /><span class="slider"></span>Earth J2</label>
    <label class="toolbarSwitch"><input v-model="scenario.forces.drag" type="checkbox" /><span class="slider"></span>Atmospheric drag</label>
    <label class="toolbarSwitch"><input v-model="scenario.forces.moon" type="checkbox" /><span class="slider"></span>Moon third-body gravity</label>
    <label class="toolbarSwitch"><input v-model="scenario.forces.sun" type="checkbox" /><span class="slider"></span>Sun third-body gravity</label>
    <label class="toolbarSwitch"><input v-model="scenario.forces.solarRadiationPressure" type="checkbox" /><span class="slider"></span>Solar radiation pressure</label>

    <div class="toolbarTitle">Visual layers</div>
    <label class="toolbarSwitch"><input v-model="scenario.magneticField" type="checkbox" /><span class="slider"></span>Earth magnetic field</label>
    <p class="propagation-panel__note">Magnetic field lines are visual guides. They do not apply Lorentz acceleration.</p>

    <details class="propagation-panel__details">
      <summary>Spacecraft assumptions</summary>
      <div class="propagation-panel__grid">
        <label>Mass <span><input v-model.number="scenario.massKg" type="number" min="1" step="1" /> kg</span></label>
        <label>Area <span><input v-model.number="scenario.areaM2" type="number" min="0.01" step="0.1" /> m²</span></label>
        <label>Cd <input v-model.number="scenario.dragCoefficient" type="number" min="0" step="0.1" /></label>
        <label>Cr <input v-model.number="scenario.reflectivityCoefficient" type="number" min="0" step="0.1" /></label>
      </div>
    </details>

    <button class="propagation-panel__run" type="button" @click="run">Run custom simulation</button>
    <p class="propagation-panel__status" :class="{ 'propagation-panel__status--error': error }">{{ error || status }}</p>
  </section>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from "vue";

import { useController } from "../composables/useController";
import type { SimulationSceneRequest } from "../modules/simulation/SimulationManager";

const cc = useController();
const status = ref("Configure a scenario and run it on the globe.");
const error = ref("");
const scenario = reactive<SimulationSceneRequest>({
  altitudeKm: 550,
  eccentricity: 0.05,
  inclinationDeg: 51.6,
  raanDeg: 35,
  argumentOfPeriapsisDeg: 0,
  trueAnomalyDeg: 0,
  durationSeconds: 7_200,
  sampleCount: 720,
  massKg: 1_000,
  areaM2: 10,
  dragCoefficient: 2.2,
  reflectivityCoefficient: 1.2,
  magneticField: false,
  forces: { gravity: true, j2: true, drag: false, moon: false, sun: false, solarRadiationPressure: false },
});

const durationMinutes = computed({
  get: () => scenario.durationSeconds / 60,
  set: (minutes: number) => {
    scenario.durationSeconds = minutes * 60;
  },
});

function run(): void {
  error.value = "";
  try {
    const samples = cc.simulations.run(scenario);
    status.value = `Simulation complete — ${samples.length.toLocaleString()} sampled states added to the Cesium clock.`;
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : "The simulation could not be propagated.";
  }
}
</script>
